/** Mintly Business Analytics — metric definitions (EN + KA). */

const metric = (title, blocks, slug) => ({
  title,
  slug,
  blocks: blocks.filter(Boolean),
});

const section = (title, metrics) => ({ title, metrics });

export const ANALYTICS_GLOSSARY = {
  en: {
    pageTitle: "Mintly Business Analytics — Metric definitions",
    intro: [
      "Date range and interval come from the Dashboard filter (e.g. today, hourly).",
      "Times follow your account timezone.",
      "Monetary values are shown in dollars, not cents.",
    ],
    sections: [
      section("Money in", [
        metric(
          "Gross Transaction Value (GTV)",
          [
          { label: "What it means", text: "Total value of completed sales before refunds and fees — similar to GMV. How much buyers paid, not how much you kept." },
          { label: "How it's calculated", text: "Sum of completed sale amounts in the selected period. Refunds and fees are not subtracted." },
          { label: "Formula", text: "GTV = sum of completed sale amounts in the selected period" },
          { label: "Use it to", text: "Answer: “How much did customers spend in my business?”" },
        ],
          "gtv"
        ),
        metric(
          "Gross Revenue",
          [
          { label: "What it means", text: "Revenue from sales and other platform income before refunds and fees — payments received on your Mintly account." },
          { label: "How it's calculated", text: "Sum of gross earnings from sales and platform income in the selected period, before refunds and fees." },
          { label: "vs GTV", text: "GTV = what customers spent. Gross Revenue = what landed as income before deductions. They can differ because of fees, taxes, processors, or non-realized revenue." },
          { label: "Use it to", text: "See total income before anything is returned or deducted." },
        ],
          "gross-revenue"
        ),
        metric(
          "Net Revenue",
          [
          { label: "What it means", text: "Revenue after refunds — what you actually kept from sales in the period." },
          { label: "Use it to", text: "Compare real earnings vs gross; pairs with GTV and refund metrics." },
        ],
          "net-revenue"
        ),
        metric(
          "MRR — Monthly Recurring Revenue",
          [
          { label: "What it means", text: "Expected monthly recurring revenue from all active and past-due subscriptions. A run-rate snapshot, not cash collected this month." },
          { label: "How it's calculated", text: "Each active/past-due subscription is normalized to a monthly amount, then summed. One-time purchases are excluded." },
          { label: "Formula", text: "MRR = sum of normalized monthly amounts · ARR = MRR × 12" },
          { label: "Use it to", text: "Gauge subscription health — watch the trend, not a single day's cash." },
        ],
          "mrr"
        ),
        metric(
          "Avg Revenue Per User (ARPU)",
          [
          { label: "What it means", text: "Average revenue per paying user (spend per paying user)." },
          { label: "Formula", text: "ARPU = revenue from customer payments ÷ number of paying customers" },
          { label: "Example", text: "$10,000 revenue / 200 paying users = $50 ARPU" },
          { label: "Use it to", text: "Assess pricing power and customer mix. Falling ARPU often means more discounts, cheaper plans, or lower-ticket buyers." },
        ],
          "arpu"
        ),
      ]),
      section("Recurring health", [
        metric(
          "Paid Active Members",
          [
          { label: "What it means", text: "Members with a paid, active membership right now." },
          { label: "Counted", text: "Paid + active memberships." },
          { label: "Not counted", text: "Free, canceled, or expired memberships." },
          { label: "Use it to", text: "Size your paying base. Compare with New Users to see if growth sticks." },
        ],
          "paid-active-members"
        ),
        metric(
          "Churn Rate",
          [
          { label: "What it means", text: "Share of paying members who canceled or otherwise left." },
          { label: "Formula", text: "Churn Rate = churned users (last 30d) ÷ (active users 30 days ago + new users in period)" },
          { label: "Use it to", text: "Measure retention. Rising New Users with flat Paid Active Members often means churn is eating growth." },
        ],
          "churn-rate"
        ),
        metric(
          "Churned Revenue",
          [
          { label: "What it means", text: "MRR lost to churn in the period." },
          { label: "How it's calculated", text: "Recurring revenue lost from memberships whose MRR decreased — cancellation, expiration, or downgrade." },
          { label: "Use it to", text: "Measure the financial cost of churn, not just headcount." },
        ],
          "churned-revenue"
        ),
      ]),
      section("Money out / risk", [
        metric(
          "Total Refunded",
          [
          { label: "What it means", text: "Total amount returned to customers in the selected period." },
          { label: "Use it to", text: "See outflows back to customers. Compare with GTV or Gross Revenue for scale." },
        ],
          "total-refunded"
        ),
        metric(
          "Refund Rate",
          [
          { label: "Formula", text: "Refund Rate = refunded paid receipts ÷ paid receipts (shown as %)" },
          { label: "Use it to", text: "Assess offer fit and refund policy. Read alongside Dispute Rate." },
        ],
          "refund-rate"
        ),
        metric(
          "Dispute Rate",
          [
          { label: "Formula", text: "Dispute Rate = disputed paid receipts ÷ paid receipts (often trailing 30 days)" },
          { label: "Use it to", text: "Monitor account health. High disputes with low refunds may mean buyers go to their bank instead of you." },
        ],
          "dispute-rate"
        ),
      ]),
      section("Customers", [
        metric(
          "New Users",
          [
          { label: "What it means", text: "People who joined/registered for the first time in the period." },
          { label: "Not counted", text: "Renewals or extra subscriptions from existing users." },
          { label: "Use it to", text: "Measure top-of-funnel acquisition vs Paid Active Members and churn." },
        ],
          "new-users"
        ),
      ]),
      section("Traffic", [
        metric(
          "Traffic over time",
          [
          { label: "What it means", text: "Visits/events in the selected interval (hourly if the dashboard is set to hourly)." },
          { label: "Sources", text: "Mintly store views and pixel events on your site. Crawlers excluded. Series capped at ~30 days." },
          { label: "Use it to", text: "See volume and trend. Traffic up but GTV flat → likely a conversion/checkout issue." },
        ],
          "traffic"
        ),
        metric("Traffic by time of day", [
          { label: "Use it to", text: "Find best times for posts, drops, livestreams, and ads." },
        ]),
        metric("Top sources", [
          { label: "How it's split", text: "direct (no campaign tag), mintly_ads, or utm_source (instagram, youtube, google, etc.)." },
        ]),
        metric("Top pages", [
          { label: "How it's split", text: "hostname + normalized path (e.g. mintly.com/your-product/pricing)." },
        ]),
        metric("Devices", [
          { label: "Categories", text: "desktop, mobile, tablet, unknown." },
        ]),
        metric(
          "Abandoned checkouts",
          [
          { label: "Formula", text: "Abandoned Checkouts = checkout starts − successful payments" },
          { label: "Use it to", text: "Find lost GTV at payment — fewer fields, clearer offer, better payment methods." },
        ],
          "abandoned-checkouts"
        ),
      ]),
    ],
    readingTogether: {
      title: "How to read metrics together",
      items: [
        { title: "High GTV, much lower Gross Revenue", text: "Check fees, taxes, refunds, processor mix." },
        { title: "Traffic up, GTV flat", text: "Likely conversion or checkout — not traffic." },
        { title: "High abandoned checkouts", text: "Friction on payment/checkout pages." },
        { title: "New Users up, Paid Active Members flat", text: "Poor conversion to paid, churn, or billing failures." },
        { title: "Churn / churned revenue rising", text: "Retention, billing failures, or member loss." },
        { title: "Disputes up vs refunds", text: "Improve refund flow before chargebacks." },
        { title: "ARPU falling", text: "More discounts, cheaper plans, or lower-ticket mix." },
      ],
    },
    important: {
      title: "Important",
      text: "Gross Revenue is not take-home pay. After Gross Revenue come refunds, disputes, processing fees, and Mintly fees. Gross Revenue ≠ net income.",
    },
    formulas: {
      title: "Quick formulas",
      lines: [
        "GTV = sum of completed sales (before refunds & fees)",
        "Gross Revenue = earnings from sales + platform income (before refunds & fees)",
        "MRR = sum of normalized monthly subscription amounts (active + past-due)",
        "ARR = MRR × 12",
        "ARPU = paying-customer revenue ÷ paying customers",
        "Churn Rate = churned (30d) ÷ (active 30d ago + new in period)",
        "Refund Rate = refunded receipts ÷ paid receipts",
        "Dispute Rate = disputed receipts ÷ paid receipts",
        "Abandoned Checkouts = checkout starts − successful payments",
      ],
    },
  },
  ka: {
    pageTitle: "MINTLY BUSINESS ANALYTICS — მეტრიკების განსაზღვრებები და გამოთვლის წესები",
    intro: [
      "დროის დიაპაზონი და ინტერვალი მოდის Dashboard-ის ფილტრიდან (მაგალითად: today, hourly).",
      "დროები მიჰყვება თქვენი ანგარიშის timezone-ს.",
      "ფულადი მნიშვნელობები მოცემულია დოლარში და არა ცენტებში.",
    ],
    sections: [
      section("MONEY IN — შემომავალი თანხა", [
        metric(
          "GROSS TRANSACTION VALUE (GTV)",
          [
          { label: "რას ნიშნავს", text: "დასრულებული გაყიდვების სრული ღირებულება, refund-ებისა და fee-ების გამოკლებამდე. იგივე იდეაა, რაც GMV — რამდენი გადაიხადეს მყიდველებმა და არა რამდენი დაგრჩათ თქვენ." },
          { label: "როგორ ითვლება", text: "არჩეულ პერიოდში დასრულებული გაყიდვების თანხების ჯამი. Refund-ები და fee-ები არ აკლდება." },
          { label: "ფორმულა", text: "GTV = არჩეულ პერიოდში დასრულებული გაყიდვების თანხების ჯამი" },
          { label: "როგორ გამოვიყენოთ", text: "„რამდენი თანხა დახარჯეს მომხმარებლებმა ჩემს ბიზნესში?“" },
        ],
          "gtv"
        ),
        metric(
          "GROSS REVENUE",
          [
          { label: "რას ნიშნავს", text: "გაყიდვებიდან და სხვა პლატფორმული შემოსავლიდან მიღებული შემოსავალი refund-ებისა და fee-ების გამოკლებამდე. MinTLY-ის ანგარიშზე განხორციელებული გადახდებიდან მიღებული შემოსავალი." },
          { label: "როგორ ითვლება", text: "არჩეულ პერიოდში გაყიდვებიდან და პლატფორმული შემოსავლიდან მიღებული gross earnings-ის ჯამი, refund-ებისა და fee-ების გამოკლებამდე." },
          { label: "განსხვავება GTV-სგან", text: "GTV = რამდენი დახარჯეს მომხმარებლებმა. Gross Revenue = რამდენი ჩაირიცხა როგორც შემოსავალი, სანამ deductions მოხდება." },
          { label: "როგორ გამოვიყენოთ", text: "მთლიანი შემოსავლის საწყისი მაჩვენებელი, სანამ თანხიდან რამე უკან დაბრუნდება ან ჩამოიჭრება." },
        ],
          "gross-revenue"
        ),
        metric(
          "NET REVENUE",
          [
          { label: "რას ნიშნავს", text: "შემოსავალი refund-ების გამოკლების შემდეგ — რეალურად რამდენი დაგრჩათ პერიოდში." },
        ],
          "net-revenue"
        ),
        metric(
          "MRR — MONTHLY RECURRING REVENUE",
          [
          { label: "რას ნიშნავს", text: "ყველა აქტიური და past-due subscription-ისგან მოსალოდნელი თვიური recurring revenue. ეს არის run-rate snapshot და არა იმ თვის განმავლობაში რეალურად მიღებული cash." },
          { label: "როგორ ითვლება", text: "თითოეული active/past-due subscription-ის თანხა ნორმალიზდება თვიურ ოდენობამდე და შემდეგ ყველა subscription ჯამდება. ერთჯერადი შესყიდვები MRR-ში არ შედის." },
          { label: "ფორმულა", text: "MRR = active + past-due subscriptions-ის თვიურ თანხამდე ნორმალიზებული თანხების ჯამი · ARR = MRR × 12" },
          { label: "როგორ გამოვიყენოთ", text: "Subscription ბიზნესის ჯანმრთელობის შესაფასებლად. ყურადღება მიაქციეთ trend-ს, და არა ერთი დღის cash-ს." },
        ],
          "mrr"
        ),
        metric(
          "AVG REVENUE PER USER (ARPU)",
          [
          { label: "რას ნიშნავს", text: "საშუალოდ რამდენი შემოსავალი მოდის თითო paying user-ზე." },
          { label: "ფორმულა", text: "ARPU = მომხმარებლების გადახდებიდან მიღებული Revenue ÷ paying customers-ის რაოდენობა" },
          { label: "მაგალითი", text: "$10,000 revenue / 200 paying users = $50 ARPU" },
          { label: "როგორ გამოვიყენოთ", text: "ფასის ძალისა და customer mix-ის შესაფასებლად." },
        ],
          "arpu"
        ),
      ]),
      section("RECURRING HEALTH — recurring ბიზნესის ჯანმრთელობა", [
        metric(
          "PAID ACTIVE MEMBERS",
          [
          { label: "რას ნიშნავს", text: "ამ მომენტში ფასიანი და აქტიური membership-ის მქონე მომხმარებლების რაოდენობა." },
          { label: "ითვლება", text: "paid + active memberships." },
          { label: "არ ითვლება", text: "free, canceled, expired." },
        ],
          "paid-active-members"
        ),
        metric(
          "CHURN RATE",
          [
          { label: "ფორმულა", text: "Churn Rate = ბოლო 30 დღეში churned users ÷ (30 დღის წინ active users + ამ პერიოდში new users)" },
          { label: "როგორ გამოვიყენოთ", text: "Retention-ის გასაზომად." },
        ],
          "churn-rate"
        ),
        metric(
          "CHURNED REVENUE",
          [
          { label: "რას ნიშნავს", text: "Churn-ის გამო დაკარგული Monthly Recurring Revenue." },
          { label: "როგორ გამოვიყენოთ", text: "Churn-ის ფინანსური ღირებულების გასაზომად." },
        ],
          "churned-revenue"
        ),
      ]),
      section("MONEY OUT / RISK — გასული თანხა / რისკი", [
        metric(
          "TOTAL REFUNDED",
          [{ label: "რას ნიშნავს", text: "არჩეულ პერიოდში მომხმარებლებისთვის დაბრუნებული თანხის სრული ოდენობა." }],
          "total-refunded"
        ),
        metric(
          "REFUND RATE",
          [{ label: "ფორმულა", text: "Refund Rate = refunded paid receipts ÷ paid receipts (%)" }],
          "refund-rate"
        ),
        metric(
          "DISPUTE RATE",
          [{ label: "ფორმულა", text: "Dispute Rate = disputed paid receipts ÷ paid receipts (%)" }],
          "dispute-rate"
        ),
      ]),
      section("CUSTOMERS — მომხმარებლები", [
        metric(
          "NEW USERS",
          [
          { label: "რას ნიშნავს", text: "ადამიანები, რომლებიც პირველად შეუერთდნენ/დარეგისტრირდნენ მოცემულ პერიოდში." },
          { label: "არ ითვლება", text: "renewals ან დამატებითი subscription არსებული მომხმარებლისგან." },
        ],
          "new-users"
        ),
      ]),
      section("TRAFFIC — ტრაფიკი", [
        metric(
          "TRAFFIC OVER TIME",
          [
          { label: "რას ნიშნავს", text: "არჩეულ ინტერვალში მიღებული visits/events." },
          { label: "წყარო", text: "mintly.com store views + pixel events თქვენს საიტზე. Crawlers გამორიცხულია." },
        ],
          "traffic"
        ),
        metric("TRAFFIC BY TIME", [
          { label: "როგორ გამოვიყენოთ", text: "posts, product drops, livestream-ები, ads — საუკეთესო დროის დასადგენად." },
        ]),
        metric("TOP SOURCES", [
          { label: "გაყოფა", text: "direct, mintly_ads, utm_source (instagram, youtube, google…)." },
        ]),
        metric("TOP PAGES", [
          { label: "გაყოფა", text: "hostname + normalized path." },
        ]),
        metric("DEVICES", [
          { label: "კატეგორიები", text: "desktop, mobile, tablet, unknown." },
        ]),
        metric(
          "ABANDONED CHECKOUTS",
          [{ label: "ფორმულა", text: "Abandoned Checkouts = checkout starts − successful payments" }],
          "abandoned-checkouts"
        ),
      ]),
    ],
    readingTogether: {
      title: "HOW TO READ THEM TOGETHER — როგორ წავიკითხოთ მეტრიკები ერთად",
      items: [
        { title: "GTV მაღალია, Gross Revenue დაბალია", text: "fees, taxes, refunds, processor mix." },
        { title: "Traffic იზრდება, GTV უცვლელია", text: "conversion/checkout პრობლემა, არა traffic." },
        { title: "Abandoned Checkouts მაღალია", text: "friction payment/checkout გვერდზე." },
        { title: "New Users იზრდება, Paid Active Members უცვლელია", text: "ცუდი კონვერსია, churn, billing failures." },
        { title: "Churn / Churned Revenue იზრდება", text: "retention ან billing პრობლემები." },
        { title: "Dispute Rate იზრდება Refund Rate-თან შედარებით", text: "გააუმჯობესეთ refund პროცესი chargeback-მდე." },
        { title: "ARPU მცირდება", text: "discount-ები, იაფი plans, დაბალი ticket-ის mix." },
      ],
    },
    important: {
      title: "IMPORTANT — მნიშვნელოვანი",
      text: "Gross Revenue არ არის Take-Home Pay. Gross Revenue-ის შემდეგ უნდა გაითვალისწინოთ: refunds, disputes, processing fees, MinTLY fees. Gross Revenue ≠ Net Income / Take-Home Pay.",
    },
    formulas: {
      title: "QUICK FORMULAS — სწრაფი ფორმულები",
      lines: [
        "GTV = დასრულებული გაყიდვების თანხების ჯამი",
        "Gross Revenue = გაყიდვებიდან + platform income (refund/fee-ის გამოკლებამდე)",
        "MRR = active + past-due subscriptions თვიურ ნორმალიზაციამდე",
        "ARR = MRR × 12",
        "ARPU = paying-customer revenue ÷ paying customers",
        "Churn Rate = churned (30d) ÷ (active 30d ago + new)",
        "Refund Rate = refunded receipts ÷ paid receipts",
        "Dispute Rate = disputed receipts ÷ paid receipts",
        "Abandoned Checkouts = checkout starts − successful payments",
      ],
    },
  },
};

export const getAnalyticsGlossary = (language) =>
  ANALYTICS_GLOSSARY[language === "ka" ? "ka" : "en"];
